import Dexie from 'dexie';

/**
 * Entity Hub Database
 * Stores local copies of Patients, Providers, Lawyers, Employers, and Insurances.
 */
export const db = new Dexie('EntityHubDB');

db.version(1).stores({
  patients: 'id, name, email, phone, updatedAt',
  providers: 'id, name, npi, speciality, updatedAt',
  lawyers: 'id, name, firm, updatedAt',
  employers: 'id, name, updatedAt',
  insurances: 'id, name, updatedAt',
  syncMetadata: 'id, lastSyncedAt' // id will be the collection name
});

export const getSyncMetadata = async (collection) => {
  const meta = await db.syncMetadata.get(collection);
  return meta?.lastSyncedAt || 0;
};

export const updateSyncMetadata = async (collection, timestamp) => {
  await db.syncMetadata.put({ id: collection, lastSyncedAt: timestamp });
};

/**
 * Upsert into collection ONLY if incoming record is newer (or equal) to local record.
 * This prevents remote deltas from overwriting newer local unsynced changes.
 */
export const upsertIfNewer = async (collectionName, entities) => {
  const entityArray = Array.isArray(entities) ? entities : [entities];
  if (!entityArray.length) return;

  const ids = entityArray.map(e => e.id).filter(Boolean);
  
  return await db.transaction('rw', db[collectionName], async () => {
    // 1. Fetch all existing records for these IDs in one go
    const existingRecords = await db[collectionName].where('id').anyOf(ids).toArray();
    const localMap = new Map(existingRecords.map(r => [r.id, r]));
    
    const toPut = [];
    
    // 2. Filter which records actually need updating
    for (const entity of entityArray) {
      if (!entity.id) continue;
      
      const local = localMap.get(entity.id);
      
      // Upsert if local doesn't exist OR incoming has newer/equal updatedAt OR locally missing updatedAt
      const needsUpdate = !local || 
                         !local.updatedAt || 
                         !entity.updatedAt || 
                         entity.updatedAt >= local.updatedAt;

      if (needsUpdate) {
        toPut.push(entity);
      }
    }

    // 3. Perform bulkPut only on records that passed the test
    if (toPut.length > 0) {
      await db[collectionName].bulkPut(toPut);
    }
  });
};

export default db;
